import React from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { useUserData } from '../../hooks/useUserData';

const AuthDebugPage: React.FC = () => {
  const auth = useAuth();
  const { user: userData } = useUserData();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await auth.signoutRedirect();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-500 to-indigo-600 p-5">
      <div className="max-w-4xl mx-auto bg-white rounded-xl p-10 shadow-2xl">
        <h1 className="text-gray-800 mb-8 text-center text-2xl font-bold">🔍 Debug de Autenticação</h1>
        
        <div className="mb-8 p-5 bg-slate-100 rounded-lg">
          <h2 className="text-gray-800 mb-4 text-xl font-semibold">Status da Autenticação</h2>
          <div className={`inline-block px-5 py-2 rounded-md font-bold mb-4 text-lg ${auth.isAuthenticated ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
            {auth.isAuthenticated ? '✅ Autenticado' : '❌ Não Autenticado'}
          </div>
          <div className="mt-4">
            <p className="my-2 text-gray-700"><strong>Carregando:</strong> {auth.isLoading ? 'Sim' : 'Não'}</p>
            <p className="my-2 text-gray-700"><strong>Erro:</strong> {auth.error ? auth.error.message : 'Nenhum'}</p>
          </div>
        </div>

        {auth.isAuthenticated && auth.user && (
          <div className="mb-8">
            <h2 className="text-gray-800 mb-5 text-xl font-semibold">Informações do Usuário</h2>
            <div className="bg-slate-100 rounded-lg p-5 mb-5">
              <h3 className="text-gray-800 mb-4 text-lg font-semibold">Dados do Token</h3>
              <pre className="bg-gray-800 text-gray-100 p-4 rounded-md overflow-x-auto text-xs leading-relaxed">
                {JSON.stringify(auth.user, null, 2)}
              </pre>
            </div>
            
            <div className="bg-slate-100 rounded-lg p-5 mb-5">
              <h3 className="text-gray-800 mb-4 text-lg font-semibold">Dados do Usuário (via useUserData hook)</h3>
              <div className="flex flex-col gap-3">
                <div className="p-3 bg-white rounded-md border-l-4 border-indigo-500">
                  <strong className="text-gray-800 block mb-1">Nome Completo:</strong> {userData?.name || 'Não disponível'}
                </div>
                <div className="p-3 bg-white rounded-md border-l-4 border-indigo-500">
                  <strong className="text-gray-800 block mb-1">Email:</strong> {userData?.email || 'Não disponível'}
                </div>
                <div className="p-3 bg-white rounded-md border-l-4 border-indigo-500">
                  <strong className="text-gray-800 block mb-1">Telefone:</strong> {userData?.phoneNumber || 'Não disponível'}
                </div>
                <div className="p-3 bg-white rounded-md border-l-4 border-indigo-500">
                  <strong className="text-gray-800 block mb-1">Sub (ID do usuário):</strong> {userData?.sub || 'Não disponível'}
                </div>
                <div className="p-3 bg-white rounded-md border-l-4 border-indigo-500">
                  <strong className="text-gray-800 block mb-1">Username:</strong> {userData?.username || 'Não disponível'}
                </div>
                <div className="p-3 bg-white rounded-md border-l-4 border-indigo-500">
                  <strong className="text-gray-800 block mb-1">Token expira em:</strong> {userData?.expiresAt ? new Date(userData.expiresAt * 1000).toLocaleString('pt-BR') : 'Não disponível'}
                </div>
              </div>
            </div>
            
            <div className="bg-slate-100 rounded-lg p-5 mb-5">
              <h3 className="text-gray-800 mb-4 text-lg font-semibold">Claims do ID Token (Raw)</h3>
              <div className="flex flex-col gap-3">
                <div className="p-3 bg-white rounded-md border-l-4 border-indigo-500">
                  <strong className="text-gray-800 block mb-1">Sub (ID do usuário):</strong> {auth.user.profile?.sub || 'Não disponível'}
                </div>
                <div className="p-3 bg-white rounded-md border-l-4 border-indigo-500">
                  <strong className="text-gray-800 block mb-1">Email:</strong> {auth.user.profile?.email || 'Não disponível'}
                </div>
                <div className="p-3 bg-white rounded-md border-l-4 border-indigo-500">
                  <strong className="text-gray-800 block mb-1">Nome:</strong> {auth.user.profile?.name || 'Não disponível'}
                </div>
                <div className="p-3 bg-white rounded-md border-l-4 border-indigo-500">
                  <strong className="text-gray-800 block mb-1">Telefone:</strong>{' '}
                  <span>{String(auth.user.profile?.phone_number ?? auth.user.profile?.phone ?? 'Não disponível')}</span>
                </div>
                <div className="p-3 bg-white rounded-md border-l-4 border-indigo-500">
                  <strong className="text-gray-800 block mb-1">Access Token:</strong> {auth.user.access_token ? `${auth.user.access_token.substring(0, 50)}...` : 'Não disponível'}
                </div>
              </div>
            </div>
          </div>
        )}

        {auth.error && (
          <div className="mb-8">
            <h2 className="text-red-600 mb-4 text-xl font-semibold">❌ Erro de Autenticação</h2>
            <div className="bg-red-100 border border-red-300 rounded-lg p-5 text-red-800">
              <p className="mb-2"><strong>Mensagem:</strong> {auth.error.message}</p>
              <p><strong>Stack:</strong></p>
              <pre className="bg-white p-4 rounded-md mt-2 text-sm overflow-x-auto text-gray-800">{auth.error.stack || 'N/A'}</pre>
            </div>
          </div>
        )}

        <div className="flex gap-4 justify-center flex-wrap mt-8">
          <button onClick={() => navigate('/dashboard')} className="px-6 py-3 border-none rounded-lg text-base font-semibold cursor-pointer transition-all duration-300 bg-blue-700 text-white hover:bg-blue-800 hover:-translate-y-0.5">
            Ir para Dashboard
          </button>
          <button onClick={() => navigate('/')} className="px-6 py-3 border-none rounded-lg text-base font-semibold cursor-pointer transition-all duration-300 bg-gray-600 text-white hover:bg-gray-700 hover:-translate-y-0.5">
            Voltar para Home
          </button>
          {auth.isAuthenticated && (
            <button onClick={handleLogout} className="px-6 py-3 border-none rounded-lg text-base font-semibold cursor-pointer transition-all duration-300 bg-red-600 text-white hover:bg-red-700 hover:-translate-y-0.5">
              Fazer Logout
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthDebugPage;

